import {
  isPreChartDraft,
  PRE_CHART_STATUS,
  type PreChartCreateInput,
} from "@rosc/contracts";
import { z } from "zod";
import { referenceClient } from "@/lib/orpc/client";
import type { RecorderFormSchemaType } from "@/schemas/newVisit.schema";

// Pre-chart (VITE_PRE_CHART_ENABLED): a visit prepared before the patient is
// seen. It is a visit with status "Pre-chart" and no recording; the note
// starts as the template's headings and is filled with the assistant.
// "Start visit" records into it (Add recording), and the note is then
// written from the recording with the pre-chart as context.
export { isPreChartDraft, PRE_CHART_STATUS };

// The pre-chart body from the template chip's values (the same template,
// style, format and examination a recording would use).
// A pre-charted visit's recordings were all added through Start visit: the
// visit itself has no transcript or duration. Ordinary visits always have a
// duration from their first recording, even while it is transcribed.
export const hasMainRecording = (
  file: { transcription?: string | null; duration?: number | null } | null,
): boolean => !!file?.transcription?.trim() || (file?.duration ?? 0) > 0;

export const preChartBody = (
  values: RecorderFormSchemaType,
  title?: string,
): PreChartCreateInput => ({
  ...(title?.trim() ? { title: title.trim() } : {}),
  template: values.templateName,
  style: values.style,
  format: values.format,
  exam: values.examination,
  ...(values.customTemplatePublicId
    ? { customTemplatePublicId: values.customTemplatePublicId }
    : {}),
});

// Changes a draft's template; the new template's headings come back for the
// rewrite (see PreChartTemplateChip).
export const changePreChartTemplate = async (
  visitPublicId: string,
  values: RecorderFormSchemaType,
  userUid: string,
): Promise<string[]> => {
  const { title: _title, ...body } = preChartBody(values);
  const response = await referenceClient.changePreChartTemplate(
    { params: { public_id: visitPublicId }, query: {}, body },
    { context: { expectedUserUid: userUid } },
  );
  return z.object({ headings: z.array(z.string()).min(1) }).parse(response)
    .headings;
};

export const createPreChart = async (
  body: PreChartCreateInput,
  identity: { key: string; userUid: string },
): Promise<string> => {
  const response = await referenceClient.createPreChart(
    { body, headers: { "idempotency-key": identity.key } },
    { context: { expectedUserUid: identity.userUid } },
  );
  return z
    .object({ public_id: z.uuidv4().toLowerCase() })
    .strict()
    .parse(response).public_id;
};

// The headings of a pre-chart note ("**Subjective**", "## Plan"), by name, in
// order, and the history heading a summary goes under by default
// (Subjective, else the history of presenting illness, else the first).
// The API decides with the same rules (packages/processing pre-chart.ts
// noteHeadingNames and preChartInsertHeading).
const HEADING_LINE = /^\s*(?:#{1,6}\s+\S.*|\*\*[^*\n]+\*\*:?)\s*$/;
const HISTORY_HEADINGS = [
  /subjective/i,
  /history of (?:the )?present/i,
  /\bhpi\b/i,
  /history/i,
];
export const preChartHeadings = (
  note: string | null | undefined,
): { headings: string[]; history: string | null } => {
  const headings = (note ?? "")
    .split("\n")
    .filter((line) => HEADING_LINE.test(line))
    .map((line) =>
      line
        .replace(/^\s*#{1,6}\s+/, "")
        .replace(/\*\*/g, "")
        .replace(/:\s*$/, "")
        .trim(),
    )
    .filter((name, index, all) => name && all.indexOf(name) === index);
  let history: string | null = null;
  for (const pattern of HISTORY_HEADINGS) {
    history = headings.find((name) => pattern.test(name)) ?? null;
    if (history) break;
  }
  return { headings, history: history ?? headings[0] ?? null };
};

// Pre-chart files whose add was undone, kept per visit in this browser so a
// reload still shows them "Undone" and offers them again (staging check
// 04/11: after a reload they read "In use"). Only source ids are kept.
const UNDONE_KEY = (visitPublicId: string) =>
  `rosc.preChart.undoneFiles:${visitPublicId}`;
export const readUndoneFiles = (visitPublicId: string): Set<number> => {
  try {
    const value: unknown = JSON.parse(
      window.localStorage.getItem(UNDONE_KEY(visitPublicId)) ?? "[]",
    );
    return new Set(
      Array.isArray(value)
        ? value.filter((id): id is number => Number.isInteger(id))
        : [],
    );
  } catch {
    return new Set();
  }
};
export const writeUndoneFiles = (
  visitPublicId: string,
  ids: ReadonlySet<number>,
): void => {
  try {
    if (ids.size)
      window.localStorage.setItem(
        UNDONE_KEY(visitPublicId),
        JSON.stringify([...ids]),
      );
    else window.localStorage.removeItem(UNDONE_KEY(visitPublicId));
  } catch {
    // Storage unavailable (private mode): kept for this session only.
  }
};
